<template>
  <v-container fluid class="pa-6">
    <!-- Header -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">Users</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Manage admin dashboard users</p>
      </div>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreate">
        Add User
      </v-btn>
    </div>

    <!-- Users Table -->
    <v-card>
      <v-data-table
        :headers="headers"
        :items="users"
        :loading="loading"
        :items-per-page="20"
      >
        <template #item.createdAt="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex justify-center" style="gap: 4px;">
            <v-btn
              icon="mdi-pencil-outline"
              variant="text"
              density="compact"
              color="primary"
              @click="openEdit(item)"
            ></v-btn>
            <v-btn
              icon="mdi-lock-reset"
              variant="text"
              density="compact"
              color="warning"
              @click="openPasswordChange(item)"
            ></v-btn>
            <v-btn
              icon="mdi-delete-outline"
              variant="text"
              density="compact"
              color="error"
              :disabled="item.id === currentUserId"
              @click="openDelete(item)"
            ></v-btn>
          </div>
        </template>

        <template #item.id="{ item }">
          <span>{{ item.id }}</span>
          <v-chip
            v-if="item.id === currentUserId"
            size="x-small"
            color="primary"
            class="ml-2"
          >
            You
          </v-chip>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create / Edit User Dialog -->
    <v-dialog v-model="showUserDialog" max-width="440px" persistent>
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon class="mr-2" :color="dialogMode === 'create' ? 'primary' : 'warning'">
            {{ dialogMode === 'create' ? 'mdi-account-plus' : 'mdi-account-edit' }}
          </v-icon>
          {{ dialogMode === 'create' ? 'Add User' : 'Edit User' }}
        </v-card-title>

        <v-card-text class="pt-2">
          <v-text-field
            v-model="userForm.username"
            label="Username"
            variant="outlined"
            density="comfortable"
            :error-messages="userFormErrors.username"
            autofocus
            @keyup.enter="saveUser"
          ></v-text-field>

          <v-text-field
            v-if="dialogMode === 'create'"
            v-model="userForm.password"
            label="Password"
            variant="outlined"
            density="comfortable"
            :type="showPassword ? 'text' : 'password'"
            :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            :error-messages="userFormErrors.password"
            class="mt-2"
            @click:append-inner="showPassword = !showPassword"
            @keyup.enter="saveUser"
          ></v-text-field>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showUserDialog = false">Cancel</v-btn>
          <v-btn
            :color="dialogMode === 'create' ? 'primary' : 'warning'"
            variant="flat"
            :loading="saving"
            @click="saveUser"
          >
            {{ dialogMode === 'create' ? 'Create' : 'Save' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Change Password Dialog -->
    <v-dialog v-model="showPasswordDialog" max-width="440px" persistent>
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="warning" class="mr-2">mdi-lock-reset</v-icon>
          Change Password
        </v-card-title>
        <v-card-text class="pt-2">
          <p class="text-body-2 text-medium-emphasis mb-4">
            Setting new password for <strong>{{ passwordTarget?.username }}</strong>
          </p>
          <v-text-field
            v-model="passwordForm.password"
            label="New Password"
            variant="outlined"
            density="comfortable"
            :type="showNewPassword ? 'text' : 'password'"
            :append-inner-icon="showNewPassword ? 'mdi-eye-off' : 'mdi-eye'"
            :error-messages="passwordFormErrors.password"
            autofocus
            @click:append-inner="showNewPassword = !showNewPassword"
          ></v-text-field>
          <v-text-field
            v-model="passwordForm.confirm"
            label="Confirm Password"
            variant="outlined"
            density="comfortable"
            :type="showConfirmPassword ? 'text' : 'password'"
            :append-inner-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
            :error-messages="passwordFormErrors.confirm"
            class="mt-2"
            @click:append-inner="showConfirmPassword = !showConfirmPassword"
            @keyup.enter="savePassword"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showPasswordDialog = false">Cancel</v-btn>
          <v-btn color="warning" variant="flat" :loading="saving" @click="savePassword">
            Update Password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="420px">
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="text-body-1">
          Are you sure you want to delete user <strong>{{ deleteTarget?.username }}</strong>?
          This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" :loading="saving" @click="deleteUser">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { userService } from "../services";
import { useNotificationStore } from "../stores";
import { useAuthStore } from "../stores";

defineOptions({ name: "UsersView" });

const notificationStore = useNotificationStore();
const authStore = useAuthStore();

const users = ref([]);
const loading = ref(false);
const saving = ref(false);

// Dialog state
const showUserDialog = ref(false);
const showPasswordDialog = ref(false);
const showDeleteDialog = ref(false);
const dialogMode = ref("create"); // 'create' | 'edit'
const editTarget = ref(null);
const passwordTarget = ref(null);
const deleteTarget = ref(null);

// Form state
const userForm = ref({ username: "", password: "" });
const userFormErrors = ref({ username: "", password: "" });
const passwordForm = ref({ password: "", confirm: "" });
const passwordFormErrors = ref({ password: "", confirm: "" });

// Password visibility
const showPassword = ref(false);
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);

const currentUserId = computed(() => authStore.user?.id ?? null);

const headers = [
  { title: "ID", key: "id", width: "80px" },
  { title: "Username", key: "username" },
  { title: "Created At", key: "createdAt" },
  { title: "Actions", key: "actions", sortable: false, align: "center" },
];

function formatDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

async function loadUsers() {
  loading.value = true;
  try {
    const response = await userService.getUsers();
    users.value = response.data || [];
  } catch (e) {
    notificationStore.handleApiError(e, "Failed to load users");
  } finally {
    loading.value = false;
  }
}

function openCreate() {
  dialogMode.value = "create";
  editTarget.value = null;
  userForm.value = { username: "", password: "" };
  userFormErrors.value = { username: "", password: "" };
  showPassword.value = false;
  showUserDialog.value = true;
}

function openEdit(user) {
  dialogMode.value = "edit";
  editTarget.value = user;
  userForm.value = { username: user.username, password: "" };
  userFormErrors.value = { username: "", password: "" };
  showUserDialog.value = true;
}

function openPasswordChange(user) {
  passwordTarget.value = user;
  passwordForm.value = { password: "", confirm: "" };
  passwordFormErrors.value = { password: "", confirm: "" };
  showNewPassword.value = false;
  showConfirmPassword.value = false;
  showPasswordDialog.value = true;
}

function openDelete(user) {
  deleteTarget.value = user;
  showDeleteDialog.value = true;
}

function validateUserForm() {
  userFormErrors.value = { username: "", password: "" };
  let valid = true;
  if (!userForm.value.username.trim()) {
    userFormErrors.value.username = "Username is required";
    valid = false;
  } else if (userForm.value.username.trim().length < 3) {
    userFormErrors.value.username = "Username must be at least 3 characters";
    valid = false;
  }
  if (dialogMode.value === "create") {
    if (!userForm.value.password) {
      userFormErrors.value.password = "Password is required";
      valid = false;
    } else if (userForm.value.password.length < 8) {
      userFormErrors.value.password = "Password must be at least 8 characters";
      valid = false;
    }
  }
  return valid;
}

function validatePasswordForm() {
  passwordFormErrors.value = { password: "", confirm: "" };
  let valid = true;
  if (!passwordForm.value.password) {
    passwordFormErrors.value.password = "Password is required";
    valid = false;
  } else if (passwordForm.value.password.length < 8) {
    passwordFormErrors.value.password = "Password must be at least 8 characters";
    valid = false;
  }
  if (!passwordForm.value.confirm) {
    passwordFormErrors.value.confirm = "Please confirm the password";
    valid = false;
  } else if (passwordForm.value.password !== passwordForm.value.confirm) {
    passwordFormErrors.value.confirm = "Passwords do not match";
    valid = false;
  }
  return valid;
}

async function saveUser() {
  if (!validateUserForm()) return;
  saving.value = true;
  try {
    if (dialogMode.value === "create") {
      await userService.createUser({
        username: userForm.value.username.trim(),
        password: userForm.value.password,
      });
      notificationStore.showSuccess("User created successfully");
    } else {
      await userService.updateUser(editTarget.value.id, {
        username: userForm.value.username.trim(),
      });
      notificationStore.showSuccess("User updated successfully");
    }
    showUserDialog.value = false;
    await loadUsers();
  } catch (e) {
    notificationStore.handleApiError(e, "Failed to save user");
  } finally {
    saving.value = false;
  }
}

async function savePassword() {
  if (!validatePasswordForm()) return;
  saving.value = true;
  try {
    await userService.updatePassword(passwordTarget.value.id, {
      password: passwordForm.value.password,
    });
    notificationStore.showSuccess("Password updated successfully");
    showPasswordDialog.value = false;
  } catch (e) {
    notificationStore.handleApiError(e, "Failed to update password");
  } finally {
    saving.value = false;
  }
}

async function deleteUser() {
  saving.value = true;
  try {
    await userService.deleteUser(deleteTarget.value.id);
    notificationStore.showSuccess("User deleted successfully");
    showDeleteDialog.value = false;
    await loadUsers();
  } catch (e) {
    notificationStore.handleApiError(e, "Failed to delete user");
  } finally {
    saving.value = false;
  }
}

onMounted(() => {
  loadUsers();
});
</script>
